import { Activities, LeadActivities, User } from '.';
import { action, computed, observable } from 'mobx';
import * as moment from 'moment';
import { app } from './../App';
import {
  ICreateActivity,
  IEditActivity,
  NewActivityStatus,
  IActivity,
} from 'common/interfaces/activity';

export class ActivitiesCreateOrEdit {
  constructor(
    private user: User,
    private activities: Activities,
    private leadActivities: LeadActivities
  ) {}

  // List of activities that are currently being edited
  @observable public editingActivities: IEditActivity[] = [];

  // Flat array of IDs of activities currently being edited
  @computed public get editingActivitiesIds(): number[] {
    return this.editingActivities.map((activity) => activity.activityId);
  }

  // New activity state
  @observable public newActivity: ICreateActivity | null = null;

  // Post new activity to the API
  @action public createActivity = async (): Promise<any> => {
    try {
      this.newActivity.status = NewActivityStatus.Saving;

      const activityService = app.service('activity');

      const dateTime = this.newActivity.date
        ? this.newActivity.date.clone()
        : null;
      let remindAtDateTime: moment.Moment | null = null;

      if (dateTime !== null) {
        dateTime.hours(this.newActivity.time.value.hours);
        dateTime.minutes(this.newActivity.time.value.minutes);

        remindAtDateTime = this.prepareReminderDateTime(
          dateTime,
          this.newActivity.remindAt !== null &&
            this.newActivity.remindAt.value !== null
            ? this.newActivity.remindAt.value
            : null
        );
      }

      const createdActivity = {
        note: this.newActivity.descriptionText,
        providerId: this.newActivity.providerId,
        activityTypeId: this.newActivity.typeId,
        activityTime: dateTime !== null ? dateTime.format() : null,
        remind: dateTime !== null && remindAtDateTime !== null ? true : false,
        remindAt: remindAtDateTime !== null ? remindAtDateTime.format() : null,
        createdByUserId: this.newActivity.createdByUserId,
        assignedToUserId: this.newActivity.assignedToUserId,
      };

      const createActivity: IActivity = await activityService.create(
        createdActivity
      );
      this.newActivity.status = NewActivityStatus.Success;

      this.leadActivities.fetchLeadActivities(this.newActivity.providerId);
      this.resetNewActivity();

      this.activities.fetchUserActivities(this.user.id);

      return true;
    } catch (err) {
      this.newActivity.status = NewActivityStatus.Error;
      console.error(err);
      return err;
    }
  };

  // Patch existing activity to the API
  @action public editActivity = async (activityId: number): Promise<any> => {
    const indexOfActivity = this.editingActivitiesIds.indexOf(activityId);
    const activity =
      indexOfActivity !== -1 ? this.editingActivities[indexOfActivity] : null;

    try {
      if (activity !== null) {
        activity.status = NewActivityStatus.Saving;

        const activityService = app.service('activity');

        const dateTime = activity.date ? activity.date.clone() : null;
        let remindAtDateTime: moment.Moment | null = null;

        if (dateTime !== null) {
          dateTime.hours(activity.time.value.hours);
          dateTime.minutes(activity.time.value.minutes);

          remindAtDateTime = this.prepareReminderDateTime(
            dateTime,
            activity.remindAt !== null && activity.remindAt.value !== null
              ? activity.remindAt.value
              : null
          );
        }

        const updatedActivity = {
          note: activity.descriptionText,
          activityTypeId: activity.typeId,
          activityTime: dateTime ? dateTime.format() : null,
          remind: dateTime !== null && remindAtDateTime !== null ? true : false,
          remindAt: remindAtDateTime ? remindAtDateTime.format() : null,
          assignedToUserId: activity.assignedToUserId,
        };

        const updateActivity: IActivity = await activityService.patch(
          activityId,
          updatedActivity
        );
        activity.status = NewActivityStatus.Success;

        this.leadActivities.fetchLeadActivities(activity.providerId);
        this.activities.fetchUserActivities(this.user.id);

        this.editingActivities.splice(indexOfActivity, 1);

        return updateActivity;
      } else {
        throw new Error('Could not find activity of such ID');
      }
    } catch (err) {
      if (activity !== null) {
        activity.status = NewActivityStatus.Error;
      }

      console.error(err);
      return err;
    }
  };

  // Start editing new activity
  @action public startEditingExistingActivity = (activityId: number) => {
    let activity: IActivity | null = null;

    // Check if activity exists in all activities
    const indexOfActivityInAllActivities: number = this.activities.allActivitiesIds.indexOf(
      activityId
    );
    if (indexOfActivityInAllActivities !== -1) {
      activity = this.activities.allActivities[indexOfActivityInAllActivities];
    } else {
      const indexOfActivityInLeadActivities: number = this.leadActivities.openLeadActivitiesIds.indexOf(
        activityId
      );
      if (indexOfActivityInLeadActivities !== -1) {
        const openLeadActivities = this.leadActivities.leadActivities[
          this.leadActivities.openLeadActivitiesIndex
        ];
        activity =
          openLeadActivities.activities[indexOfActivityInLeadActivities];
      }
    }

    // Check if activity has been found and copy it to editing array
    if (activity !== null) {
      const dateTime = activity.activityTime
        ? moment(activity.activityTime)
        : null;

      this.editingActivities.push({
        activityId,
        providerId: activity.providerId,
        typeId: activity.activityTypeId,
        typeName: activity.activityType.name,
        date: dateTime,
        time: {
          value: dateTime
            ? {
                hours: dateTime.hours(),
                minutes: dateTime.minutes(),
                totalMinutes: dateTime.hours() * 60 + dateTime.minutes(),
              }
            : null,
          label: dateTime ? dateTime.format('hh:mm A') : 'No date selected',
        },
        remindAt: {
          value: null,
          label: 'Do not remind',
        },
        assignedToUserId: activity.assignedToUserId,
        assignedToUserName: activity.assignedToUser.name,
        createdByUserId: activity.createdByUserId,
        descriptionHtml: '<p>' + activity.note + '</p>',
        descriptionText: activity.note,
        descriptionTooLong: false,
        status: NewActivityStatus.None,
      });
    }
  };

  // Initialize new activity
  @action public initializeNewActivity = (providerId: number) => {
    const now = moment();
    this.newActivity = {
      providerId,
      typeId: null,
      typeName: null,
      date: null,
      time: {
        value: {
          hours: now.hours(),
          minutes: now.minutes(),
          totalMinutes: now.hours() * 60 + now.minutes(),
        },
        label: now.format('hh:mm A'),
      },
      remindAt: {
        value: null,
        label: 'Do not remind',
      },
      assignedToUserId: this.user.id,
      assignedToUserName: this.user.name,
      createdByUserId: this.user.id,
      descriptionHtml: '',
      descriptionText: '',
      descriptionTooLong: false,
      status: NewActivityStatus.None,
    };
  };

  // Reset activity
  @action public resetNewActivity = () => {
    this.newActivity = null;
  };

  // Prepare reminder date time
  private prepareReminderDateTime = (
    activityDateTime: moment.Moment,
    remindAtValue: number | null
  ): moment.Moment | null => {
    let remindAtDateTime: moment.Moment | null = null;
    if (remindAtValue !== null) {
      remindAtDateTime = activityDateTime.clone();
      if (typeof remindAtValue === 'string') {
        remindAtDateTime =
          remindAtValue === 'dontremind'
            ? null
            : remindAtValue === 'atthetime'
            ? remindAtDateTime
            : null;
      } else if (typeof remindAtValue === 'number') {
        if (remindAtValue >= 0) {
          remindAtDateTime.subtract(remindAtValue, 'minutes');
        } else if (remindAtValue < 0) {
          remindAtDateTime.add(remindAtValue, 'minutes');
        }
      }
    }
    return remindAtDateTime;
  };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/stores/ActivitiesCreateOrEdit.ts